import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useDialogo } from "./dialogo.js";
import { Ayuda, Caja, Serie } from "./graficos.js";

/**
 * El panel SEO «a fondo» (26/10/2026).
 *
 * Jorge: «tiene que estar a la altura de Semrush o Ahrefs… un panel que pueda
 * usar para revisar keywords, estados de proyecto, actualizar estrategias sobre
 * la marcha, controlar los contenidos de cada sección o blog… que realmente
 * sirva para mejorar desde ahí y gestionar el SEO de manera super profesional».
 *
 * Aquí van las vistas que faltaban y que esas herramientas ponen en el centro:
 * las páginas, las búsquedas a fondo (canibalización, oportunidades, página que
 * sale frente a la elegida), la competencia, el mapa de secciones y el trabajo
 * (acciones y objetivos). Todo lee lo guardado: abrir no pregunta a Google.
 */

// ── Utilidades ─────────────────────────────────────────────────────────────

function useJson<T>(url: string | null): { datos: T | null; cargando: boolean; error: string | null; recargar: () => void } {
  const [datos, setDatos] = useState<T | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    if (!url) return;
    let vivo = true;
    setCargando(true);
    setError(null);
    fetch(url, { credentials: "same-origin" })
      .then(async (r) => {
        const j = (await r.json().catch(() => ({}))) as T & { error?: string };
        if (!r.ok) throw new Error(j.error ?? `El servidor contestó ${r.status}`);
        if (vivo) setDatos(j);
      })
      .catch((e: Error) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [url, vuelta]);
  return { datos, cargando, error, recargar: useCallback(() => setVuelta((v) => v + 1), []) };
}

async function mandar(url: string, metodo: string, cuerpo?: unknown): Promise<{ ok: boolean; datos: Record<string, unknown> }> {
  const r = await fetch(url, {
    method: metodo,
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
  });
  const datos = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  return { ok: r.ok, datos };
}

const n = (x: number | null | undefined) => (x === null || x === undefined ? "—" : x.toLocaleString("es-ES", { maximumFractionDigits: 1 }));

/** «1.240 €». Null = sin precio en Google Ads. */
const eur = (x: number | null | undefined) => (x === null || x === undefined ? "—" : `${x.toLocaleString("es-ES")} €`);
const eurClic = (x: number | null | undefined) =>
  x === null || x === undefined ? "—" : `${x.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

interface Ads {
  busquedas: number;
  competencia: string | null;
  cpc: number | null;
}

/** La ruta para leerla: sin dominio, «portada» para la raíz. */
export function rutaLegible(url: string): string {
  const r = url.replace(/^https?:\/\/(www\.)?[^/]+/i, "");
  /* La versión sin candado de una página es OTRA URL para Google: se dice. */
  const http = /^http:\/\//i.test(url) ? "http · " : "";
  if (!r || r === "/") return `${http}portada`;
  try {
    return http + decodeURIComponent(r.replace(/\/$/, ""));
  } catch {
    return http + r;
  }
}

/** ▲ 12 / ▼ 3 / =, con el color de lo que pasó. `menosEsMejor` para posiciones. */
function Cambio({ ahora, antes, menosEsMejor = false }: { ahora: number | null; antes: number | null; menosEsMejor?: boolean }) {
  if (ahora === null || antes === null) return <span className="sin">·</span>;
  const d = Math.round((ahora - antes) * 10) / 10;
  if (d === 0) return <span className="sin">=</span>;
  const bien = menosEsMejor ? d < 0 : d > 0;
  return (
    <span className={bien ? "mejor" : "peor"}>
      {(menosEsMejor ? d < 0 : d > 0) ? "▲" : "▼"} {Math.abs(d).toLocaleString("es-ES")}
    </span>
  );
}

function Cifra({ valor, que, ayuda }: { valor: ReactNode; que: string; ayuda?: string }) {
  return (
    <div className="fondo-cifra">
      <strong>{valor}</strong>
      <span>
        {que}
        {ayuda && <Ayuda texto={ayuda} />}
      </span>
    </div>
  );
}

function Cargando({ cargando, error, vacio }: { cargando: boolean; error: string | null; vacio?: string | null }) {
  if (error) return <p className="peor">{error}</p>;
  if (cargando) return <p className="pista">Cargando…</p>;
  if (vacio) return <p className="grafico-vacio">{vacio}</p>;
  return null;
}

function mesCorto(mes: string): string {
  const nombres = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const [a, m] = mes.split("-").map(Number) as [number, number];
  return `${nombres[m - 1]} ${a}`;
}

// ── Páginas ────────────────────────────────────────────────────────────────

interface Pagina {
  url: string;
  ruta: string;
  clics: number;
  impresiones: number;
  ctr: number | null;
  posicion: number | null;
  sesiones: number | null;
  conInteres: number | null;
  clicsAntes: number | null;
  posicionAntes: number | null;
  busquedas: number;
  /** Lo que costarían en anuncios sus clics del mes (€). Null = sin precio. */
  valor: number | null;
  top: { consulta: string; clics: number; posicion: number | null }[];
  objetivoDe: string[];
  fallos: string[];
  entrantes: number | null;
  huerfana: boolean;
  enSitemap: boolean | null;
}

interface DatosDePaginas {
  mes: string | null;
  meses: string[];
  enCurso: boolean;
  paginas: Pagina[];
  totales: { paginas: number; conClics: number; clics: number; impresiones: number; sesiones: number | null; valor: number | null; busquedasConPrecio?: number; busquedasMiradas?: number } | null;
  zona?: string;
}

const FILTROS_DE_PAGINA = [
  ["todas", "Todas"],
  ["objetivo", "De la estrategia"],
  ["fallos", "Con fallos"],
  ["huerfanas", "Huérfanas"],
  ["sitemap", "Fuera del sitemap"],
  ["sinclics", "Se enseñan y no reciben clics"],
] as const;
type FiltroDePagina = (typeof FILTROS_DE_PAGINA)[number][0];

type Orden = "clics" | "impresiones" | "ctr" | "posicion" | "sesiones" | "busquedas" | "valor";

/** Aviso del mes que se ve, cuando no es el del selector o va a medias. */
function DeQueMes({ mes, enCurso, pedido }: { mes: string | null; enCurso: boolean; pedido: string | null }) {
  if (!mes) return null;
  return (
    <p className="pista">
      Search Console de <strong>{mesCorto(mes)}</strong>
      {enCurso && " · el mes en curso, todavía a medias"}
      {pedido && pedido !== mes && ` · de ${mesCorto(pedido)} no hay datos de páginas, se enseña el último que hay`}
    </p>
  );
}

export function PaginasDelCliente({ clienteId, mes }: { clienteId: string; mes: string | null }) {
  const { datos, cargando, error } = useJson<DatosDePaginas>(
    `/api/memoria/clientes/${clienteId}/seo/paginas${mes ? `?mes=${mes}` : ""}`,
  );
  const [filtro, setFiltro] = useState<FiltroDePagina>("todas");
  const [busca, setBusca] = useState("");
  const [orden, setOrden] = useState<Orden>("clics");
  const [abierta, setAbierta] = useState<string | null>(null);
  const [cuantas, setCuantas] = useState(100);

  const filas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const lista = (datos?.paginas ?? []).filter((p) => {
      if (t && !p.ruta.includes(t) && !p.top.some((q) => q.consulta.includes(t))) return false;
      if (filtro === "objetivo") return p.objetivoDe.length > 0;
      if (filtro === "fallos") return p.fallos.length > 0;
      if (filtro === "huerfanas") return p.huerfana;
      if (filtro === "sitemap") return p.enSitemap === false;
      if (filtro === "sinclics") return p.clics === 0 && p.impresiones >= 100;
      return true;
    });
    const valor = (p: Pagina): number => {
      if (orden === "posicion") return -(p.posicion ?? 999);
      if (orden === "ctr") return p.ctr ?? -1;
      if (orden === "sesiones") return p.sesiones ?? -1;
      if (orden === "valor") return p.valor ?? -1;
      return p[orden];
    };
    return [...lista].sort((a, b) => valor(b) - valor(a));
  }, [datos, filtro, busca, orden]);

  if (!datos || !datos.mes)
    return <Cargando cargando={cargando} error={error} vacio={datos && !datos.mes ? "Todavía no hay páginas guardadas de este cliente. Salen de Search Console: hace falta tenerlo conectado en «Conexiones»." : null} />;

  const t = datos.totales!;
  const principal = origenPrincipal(datos.paginas);
  const cuenta = (f: FiltroDePagina) =>
    f === "todas" ? datos.paginas.length
    : f === "objetivo" ? datos.paginas.filter((p) => p.objetivoDe.length > 0).length
    : f === "fallos" ? datos.paginas.filter((p) => p.fallos.length > 0).length
    : f === "huerfanas" ? datos.paginas.filter((p) => p.huerfana).length
    : f === "sitemap" ? datos.paginas.filter((p) => p.enSitemap === false).length
    : datos.paginas.filter((p) => p.clics === 0 && p.impresiones >= 100).length;

  const Th = ({ o, children, ayuda }: { o: Orden; children: ReactNode; ayuda: string }) => (
    <th className={`num ${orden === o ? "ordenada" : ""}`}>
      <button type="button" className="ordenar" onClick={() => setOrden(o)} title="Ordenar por aquí">
        {children}
        {orden === o ? " ↓" : ""}
      </button>
      <Ayuda texto={ayuda} />
    </th>
  );

  return (
    <div className="fondo">
      <DeQueMes mes={datos.mes} enCurso={datos.enCurso} pedido={mes} />
      <div className="fondo-cifras">
        <Cifra valor={n(t.conClics)} que={`páginas con clics, de ${n(t.paginas)}`} ayuda="Páginas que recibieron al menos un clic desde Google este mes, de todas las que Google enseñó." />
        <Cifra valor={n(t.clics)} que="clics desde Google" />
        <Cifra valor={n(t.impresiones)} que="veces que se enseñaron" />
        {t.valor !== null && (
          <Cifra
            valor={eur(t.valor)}
            que="al mes en anuncios"
            ayuda={`Lo que costaría conseguir con Google Ads los clics que ya llegan gratis desde Google este mes (precio por clic de Google Ads en ${datos.zona ?? "su zona"}, o en España si allí no hay). Es el «valor del tráfico» de Semrush y Ahrefs. Sólo tienen precio ${t.busquedasConPrecio ?? "?"} de las ${t.busquedasMiradas ?? "?"} búsquedas que más se enseñan: las muy concretas no tienen anunciantes y no suman.`}
          />
        )}
        {t.sesiones !== null && <Cifra valor={n(t.sesiones)} que="visitas de Google (Analytics)" ayuda="Visitas que llegaron desde la búsqueda orgánica, según Analytics, por la página por la que entraron. Sólo cuentan quienes aceptaron las cookies." />}
      </div>

      <div className="fondo-filtros">
        {FILTROS_DE_PAGINA.map(([id, nombre]) => (
          <button key={id} type="button" className={filtro === id ? "chip activo" : "chip"} onClick={() => setFiltro(id)}>
            {nombre} <span className="n">{cuenta(id)}</span>
          </button>
        ))}
        <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar una página o una búsqueda…" aria-label="Buscar página" />
      </div>

      <div className="tabla-envoltura">
        <table className="tabla-fondo">
          <thead>
            <tr>
              <th>Página</th>
              <Th o="clics" ayuda="Clics desde Google este mes, y cuánto cambió respecto al anterior.">Clics</Th>
              <Th o="impresiones" ayuda="Cuántas veces la enseñó Google en los resultados.">Impr.</Th>
              <Th o="ctr" ayuda="De cada cien veces que se enseña, cuántas pulsan. Muy bajo con muchas impresiones: el título o la descripción no convencen.">CTR</Th>
              <Th o="posicion" ayuda="Posición media en Google de todas sus búsquedas. Más bajo es mejor.">Pos.</Th>
              <Th o="sesiones" ayuda="Visitas que entraron por esta página desde Google, según Analytics.">Visitas</Th>
              <Th o="busquedas" ayuda="Por cuántas búsquedas distintas sale, y la que más clics le da.">Búsquedas</Th>
              <Th o="valor" ayuda="Lo que costarían en anuncios los clics que trae esta página este mes. Dice qué páginas valen dinero, no sólo visitas.">Valor</Th>
              <th>Señales</th>
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr>
                <td colSpan={9} className="sin-nada">Ninguna página con ese filtro.</td>
              </tr>
            )}
            {filas.slice(0, cuantas).map((p) => (
              <FilaDePagina key={p.url} principal={principal} p={p} clienteId={clienteId} mes={datos.mes!} abierta={abierta === p.url} alPulsar={() => setAbierta(abierta === p.url ? null : p.url)} />
            ))}
          </tbody>
        </table>
      </div>
      {filas.length > cuantas && (
        <button type="button" className="acc sec" onClick={() => setCuantas((c) => c + 200)}>
          Ver {Math.min(200, filas.length - cuantas)} más (quedan {filas.length - cuantas})
        </button>
      )}
    </div>
  );
}

/** El origen más común de la lista: el «normal» de la web. Los demás se enseñan. */
function origenPrincipal(paginas: Pagina[]): string | null {
  const cuenta = new Map<string, number>();
  for (const p of paginas) {
    try {
      const o = new URL(p.url).origin;
      cuenta.set(o, (cuenta.get(o) ?? 0) + p.impresiones + 1);
    } catch {
      /* nada */
    }
  }
  return [...cuenta.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/** «portada», o «http://gesclean.es · portada» si no es la versión normal de la web. */
function nombreDePagina(url: string, principal: string | null): string {
  try {
    const o = new URL(url).origin;
    if (principal && o !== principal) return `${o.replace(/^https:\/\//, "")} · ${rutaLegible(url)}`;
  } catch {
    /* nada */
  }
  return rutaLegible(url);
}

function FilaDePagina({ p, clienteId, mes, abierta, alPulsar, principal }: { p: Pagina; clienteId: string; mes: string; abierta: boolean; alPulsar: () => void; principal: string | null }) {
  return (
    <>
      <tr className={abierta ? "abierta" : undefined} onClick={alPulsar}>
        <td className="pagina">
          <button type="button" className="kw-txt" aria-expanded={abierta} onClick={(e) => (e.stopPropagation(), alPulsar())}>
            {nombreDePagina(p.url, principal)}
          </button>
          <a href={p.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Abrir la página" className="salir">
            ↗
          </a>
        </td>
        <td className="num">
          <strong>{n(p.clics)}</strong> <Cambio ahora={p.clics} antes={p.clicsAntes} />
        </td>
        <td className="num">{n(p.impresiones)}</td>
        <td className="num">{p.ctr === null ? "—" : `${p.ctr.toLocaleString("es-ES")} %`}</td>
        <td className="num">
          {n(p.posicion)} <Cambio ahora={p.posicion} antes={p.posicionAntes} menosEsMejor />
        </td>
        <td className="num">{n(p.sesiones)}</td>
        <td className="num">
          {p.busquedas}
          {p.top[0] && <span className="sub">{p.top[0].consulta}</span>}
        </td>
        <td className="num">{p.valor ? eur(p.valor) : <span className="sin">—</span>}</td>
        <td className="senales">
          {p.objetivoDe.length > 0 && <span className="pill mejor" title={p.objetivoDe.join(", ")}>★ {p.objetivoDe.length} de la estrategia</span>}
          {p.fallos.length > 0 && <span className="pill peor" title={[...new Set(p.fallos)].join(", ")}>{p.fallos.length} fallos</span>}
          {p.huerfana && <span className="pill peor" title="Ninguna página de la web enlaza a ésta">huérfana</span>}
          {p.enSitemap === false && <span className="pill aviso" title="No está en el sitemap que declara la web">fuera del sitemap</span>}
          {p.entrantes !== null && <span className="pill" title="Enlaces internos que recibe">{p.entrantes} enlaces internos</span>}
        </td>
      </tr>
      {abierta && (
        <tr className="detalle">
          <td colSpan={9}>
            <DetalleDePagina clienteId={clienteId} url={p.url} mes={mes} />
          </td>
        </tr>
      )}
    </>
  );
}

interface DatosDePagina {
  serie: { mes: string; clics: number; impresiones: number; posicion: number | null; sesiones: number | null }[];
  busquedas: { consulta: string; clics: number; impresiones: number; posicion: number | null; clicsAntes: number | null; posicionAntes: number | null }[];
  fallos: { tipo: string; detalle: string | null; estado: string; detectado: string; resuelto: string | null; reaperturas: number }[];
}

function DetalleDePagina({ clienteId, url, mes }: { clienteId: string; url: string; mes: string }) {
  const { datos, cargando, error } = useJson<DatosDePagina>(
    `/api/memoria/clientes/${clienteId}/seo/pagina?url=${encodeURIComponent(url)}&mes=${mes}`,
  );
  if (!datos) return <Cargando cargando={cargando} error={error} />;
  const abiertos = datos.fallos.filter((f) => f.estado === "abierto");
  return (
    <div className="detalle-pagina">
      <div className="dp-graficos">
        <div>
          <h4>Clics al mes</h4>
          <Serie puntos={datos.serie.map((s) => ({ mes: s.mes, valor: s.clics }))} formato={(v) => `${v} clics`} />
        </div>
        <div>
          <h4>Posición media</h4>
          <Serie puntos={datos.serie.map((s) => ({ mes: s.mes, valor: s.posicion }))} menosEsMejor formato={(v) => `posición ${v}`} />
        </div>
      </div>
      <h4>Las búsquedas por las que sale ({datos.busquedas.length})</h4>
      <div className="tabla-envoltura">
        <table className="tabla-fondo compacta">
          <thead>
            <tr>
              <th>Búsqueda</th>
              <th className="num">Clics</th>
              <th className="num">Impr.</th>
              <th className="num">Pos.</th>
            </tr>
          </thead>
          <tbody>
            {datos.busquedas.slice(0, 40).map((b) => (
              <tr key={b.consulta}>
                <td>{b.consulta}</td>
                <td className="num">
                  {n(b.clics)} <Cambio ahora={b.clics} antes={b.clicsAntes} />
                </td>
                <td className="num">{n(b.impresiones)}</td>
                <td className="num">
                  {n(b.posicion)} <Cambio ahora={b.posicion} antes={b.posicionAntes} menosEsMejor />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {datos.fallos.length > 0 && (
        <>
          <h4>
            Fallos técnicos: {abiertos.length} abiertos
            {datos.fallos.length > abiertos.length && `, ${datos.fallos.length - abiertos.length} ya arreglados`}
          </h4>
          <ul className="lista-fallos">
            {datos.fallos.map((f, i) => (
              <li key={i} className={f.estado === "abierto" ? "peor" : "sin"}>
                {f.tipo}
                {f.detalle && ` — ${f.detalle}`} · desde {f.detectado}
                {f.resuelto && ` · arreglado ${f.resuelto}`}
                {f.reaperturas > 0 && ` · ha vuelto ${f.reaperturas} ${f.reaperturas === 1 ? "vez" : "veces"}`}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Búsquedas a fondo ───────────────────────────────────────────────────────

interface Busqueda {
  consulta: string;
  clics: number;
  impresiones: number;
  posicion: number | null;
  paginas: number;
  principal: string;
  ads?: Ads | null;
}

interface DatosDeBusquedas {
  mes: string | null;
  meses: string[];
  enCurso: boolean;
  totales: { busquedas: number; clics: number; impresiones: number; enTop3: number; enPrimeraPagina: number };
  zona?: string;
  ads?: { conectado: boolean; error: string | null; conPrecio: number; miradas?: number };
  valorDelTrafico?: number | null;
  busquedas: Busqueda[];
  canibalizacion: {
    consulta: string;
    impresiones: number;
    clics: number;
    ads?: Ads | null;
    paginas: { url: string; impresiones: number; clics: number; posicion: number | null; parte: number }[];
  }[];
  oportunidades: {
    lista: { consulta: string; posicion: number; impresiones: number; clics: number; objetivo: number; siLoConsigue: number; url: string | null; ads?: Ads | null; euros?: number | null }[];
    cerca: { consulta: string; posicion: number; impresiones: number; clics: number; url: string | null; ads?: Ads | null; clicsSiTop3?: number; euros?: number | null }[];
    miradas: number;
  };
  cambios: {
    nuevas: Busqueda[];
    perdidas: Busqueda[];
    suben: (Busqueda & { antes: number; gana: number })[];
    bajan: (Busqueda & { antes: number; gana: number })[];
  };
  desalineadas: { keyword: string; objetivo: string; enGoogle: string; fuente: string; posicion: number | null }[];
}

export function BusquedasAFondo({ clienteId, mes }: { clienteId: string; mes: string | null }) {
  const { datos, cargando, error } = useJson<DatosDeBusquedas>(
    `/api/memoria/clientes/${clienteId}/seo/busquedas${mes ? `?mes=${mes}` : ""}`,
  );
  const [busca, setBusca] = useState("");
  const [cuantas, setCuantas] = useState(50);
  if (!datos || !datos.mes) return <Cargando cargando={cargando} error={error} vacio={datos && !datos.mes ? "Sin datos de Search Console guardados todavía." : null} />;
  const t = datos.totales;
  const filtradas = datos.busquedas.filter((b) => !busca.trim() || b.consulta.includes(busca.trim().toLowerCase()));

  return (
    <div className="fondo">
      <h3 className="fondo-titulo">Lo que busca la gente (Search Console)</h3>
      <DeQueMes mes={datos.mes} enCurso={datos.enCurso} pedido={mes} />
      <div className="fondo-cifras">
        <Cifra valor={n(t.busquedas)} que="búsquedas por las que sale" ayuda="Búsquedas distintas con al menos 3 impresiones este mes. Las de una o dos impresiones no se guardan: no sirven para decidir nada." />
        <Cifra valor={n(t.enTop3)} que="en el top 3" />
        <Cifra valor={n(t.enPrimeraPagina)} que="en primera página" />
        <Cifra valor={n(t.clics)} que="clics" />
        {datos.valorDelTrafico !== null && datos.valorDelTrafico !== undefined && (
          <Cifra
            valor={eur(datos.valorDelTrafico)}
            que="al mes en anuncios"
            ayuda={`Lo que costaría pagar en Google Ads los clics que ya llegan gratis por estas búsquedas (precio por clic en ${datos.zona ?? "su zona"}, o en España si allí no hay). Tienen precio ${datos.ads?.conPrecio ?? "?"} de las ${datos.ads?.miradas ?? "?"} que más se enseñan; las demás no suman.`}
          />
        )}
      </div>
      {datos.ads && !datos.ads.conectado && <p className="pista">Sin precios de Google Ads ahora mismo: {datos.ads.error}</p>}

      <div className="rejilla-fondo">
        <Caja
          titulo="A un empujón de la primera página"
          fuente="Search Console"
          ayuda="Búsquedas en las que ya sales cerca de arriba (entre el puesto 4 y el 20) y con gente buscándolas. Subirlas unos puestos es lo que más clics trae con menos trabajo: suele bastar con mejorar el título, el contenido de esa página y algún enlace interno."
        >
          {datos.oportunidades.lista.length === 0 && datos.oportunidades.cerca.length === 0 ? (
            <p className="pista">Ninguna de las {n(datos.oportunidades.miradas)} búsquedas está entre el puesto 4 y el 20 con volumen.</p>
          ) : datos.oportunidades.lista.length === 0 ? (
            <div className="tabla-envoltura">
              <p className="pista">Entre el puesto 4 y el 20, de las que más se enseñan a las que menos:</p>
              <table className="tabla-fondo compacta">
                <thead>
                  <tr>
                    <th>Búsqueda</th>
                    <th className="num">Pos.</th>
                    <th className="num">Impr.</th>
                    <th className="num">
                      Si sube al 3º <Ayuda texto="Clics de más al mes si subiera al tercer puesto, con sus impresiones de verdad, y lo que valen a precio de Google Ads." />
                    </th>
                    <th>Página</th>
                  </tr>
                </thead>
                <tbody>
                  {[...datos.oportunidades.cerca]
                    .sort((a, b) => (b.euros ?? -1) - (a.euros ?? -1) || b.impresiones - a.impresiones)
                    .slice(0, 15)
                    .map((o) => (
                    <tr key={o.consulta}>
                      <td>
                        {o.consulta}
                        {o.ads && <span className="sub">{n(o.ads.busquedas)}/mes · {eurClic(o.ads.cpc)}/clic</span>}
                      </td>
                      <td className="num">{n(o.posicion)}</td>
                      <td className="num">{n(o.impresiones)}</td>
                      <td className="num">
                        <span className="mejor">+{n(o.clicsSiTop3 ?? 0)} clics</span>
                        {o.euros ? <span className="sub">{eur(o.euros)}/mes</span> : null}
                      </td>
                      <td className="url">{o.url ? rutaLegible(o.url) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tabla-envoltura">
              <table className="tabla-fondo compacta">
                <thead>
                  <tr>
                    <th>Búsqueda</th>
                    <th className="num">Pos.</th>
                    <th className="num">Impr.</th>
                    <th className="num">Si sube al {""}</th>
                    <th>Página</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.oportunidades.lista.slice(0, 15).map((o) => (
                    <tr key={o.consulta}>
                      <td>{o.consulta}</td>
                      <td className="num">{n(o.posicion)}</td>
                      <td className="num">{n(o.impresiones)}</td>
                      <td className="num">
                        <span className="mejor">+{n(o.siLoConsigue)} clics</span>
                        <span className="sub">
                          al {o.objetivo}º{o.euros ? ` · ${eur(o.euros)}/mes` : ""}
                        </span>
                      </td>
                      <td className="url">{o.url ? rutaLegible(o.url) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Caja>

        <Caja
          titulo="Canibalización: páginas tuyas peleando por la misma búsqueda"
          fuente="Search Console"
          ayuda="Búsquedas en las que Google reparte las impresiones entre dos o más páginas de la web. Ninguna se lleva la fuerza entera y las dos se quedan más abajo. Lo normal es elegir una, reforzarla y que la otra enlace a ella (o unirlas)."
        >
          {datos.canibalizacion.length === 0 ? (
            <p className="pista">Ninguna búsqueda con volumen está repartida entre varias páginas. Bien.</p>
          ) : (
            <div className="canibal">
              {datos.canibalizacion.slice(0, 12).map((c) => (
                <div className="canibal-fila" key={c.consulta}>
                  <div className="canibal-q">
                    <strong>{c.consulta}</strong>
                    <span className="pista">
                      {n(c.impresiones)} impr. · {n(c.clics)} clics
                      {c.ads && ` · ${n(c.ads.busquedas)}/mes en la zona${c.ads.cpc ? ` · ${eurClic(c.ads.cpc)}/clic` : ""}`}
                    </span>
                  </div>
                  {c.paginas.map((p) => (
                    <div className="canibal-p" key={p.url}>
                      <span className="barra" style={{ width: `${Math.max(4, p.parte)}%` }} />
                      <span className="url" title={p.url}>
                        {rutaLegible(p.url)}
                      </span>
                      <span className="sub">
                        {p.parte} % · pos. {n(p.posicion)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Caja>

        <Caja
          titulo="Google enseña otra página que la elegida"
          ayuda="Keywords de la estrategia a las que les asignaste una página, pero por las que Google enseña otra de la web. Mientras pase, la página elegida no sube: o se refuerza (contenido y enlaces internos hacia ella) o se cambia la página elegida."
        >
          {datos.desalineadas.length === 0 ? (
            <p className="pista">En todas las keywords de la estrategia con página, Google enseña la que se eligió (o todavía no la enseña ninguna).</p>
          ) : (
            <div className="tabla-envoltura">
              <table className="tabla-fondo compacta">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>La elegida</th>
                    <th>La que sale</th>
                    <th className="num">Pos.</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.desalineadas.map((d) => (
                    <tr key={d.keyword}>
                      <td>{d.keyword}</td>
                      <td className="url">{rutaLegible(d.objetivo)}</td>
                      <td className="url peor" title={d.fuente === "search-console" ? "Según Search Console" : "Según SE Ranking"}>
                        {rutaLegible(d.enGoogle)}
                      </td>
                      <td className="num">{n(d.posicion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Caja>

        <Caja titulo="Lo que ha cambiado este mes" ayuda="Búsquedas con al menos 30 impresiones: las que suben o bajan tres puestos o más, las que aparecen por primera vez y las que han desaparecido.">
          <div className="cambios-4">
            <ListaDeCambios titulo="Suben" tono="mejor" filas={datos.cambios.suben.map((b) => ({ q: b.consulta, d: `${n(b.antes)} → ${n(b.posicion)}` }))} />
            <ListaDeCambios titulo="Bajan" tono="peor" filas={datos.cambios.bajan.map((b) => ({ q: b.consulta, d: `${n(b.antes)} → ${n(b.posicion)}` }))} />
            <ListaDeCambios titulo="Nuevas" tono="mejor" filas={datos.cambios.nuevas.map((b) => ({ q: b.consulta, d: `pos. ${n(b.posicion)}` }))} />
            <ListaDeCambios titulo="Perdidas" tono="peor" filas={datos.cambios.perdidas.map((b) => ({ q: b.consulta, d: `era ${n(b.posicion)}` }))} />
          </div>
        </Caja>
      </div>

      <Caja titulo={`Todas las búsquedas (${n(datos.busquedas.length)})`} fuente="Search Console" ayuda="Todo lo que la gente escribió en Google y por lo que salió la web este mes, de más clics a menos. «Páginas» dice entre cuántas URLs se reparte.">
        <div className="barra-buscar">
          <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar una búsqueda…" aria-label="Buscar búsqueda" />
          <span className="pista">{n(filtradas.length)}</span>
        </div>
        <div className="tabla-envoltura">
          <table className="tabla-fondo compacta">
            <thead>
              <tr>
                <th>Búsqueda</th>
                <th className="num">Clics</th>
                <th className="num">Impr.</th>
                <th className="num">CTR</th>
                <th className="num">Pos.</th>
                <th className="num">
                  Al mes <Ayuda texto="Búsquedas al mes en la zona del cliente, según Google Ads." />
                </th>
                <th className="num">€/clic</th>
                <th className="num">Páginas</th>
                <th>La que más sale</th>
              </tr>
            </thead>
            <tbody>
              {filtradas.slice(0, cuantas).map((b) => (
                <tr key={b.consulta}>
                  <td>{b.consulta}</td>
                  <td className="num">{n(b.clics)}</td>
                  <td className="num">{n(b.impresiones)}</td>
                  <td className="num">{b.impresiones ? `${((b.clics / b.impresiones) * 100).toLocaleString("es-ES", { maximumFractionDigits: 1 })} %` : "—"}</td>
                  <td className="num">{n(b.posicion)}</td>
                  <td className="num">{b.ads ? n(b.ads.busquedas) : <span className="sin">—</span>}</td>
                  <td className="num">{b.ads?.cpc ? eurClic(b.ads.cpc) : <span className="sin">—</span>}</td>
                  <td className={`num ${b.paginas > 1 ? "aviso" : ""}`}>{b.paginas}</td>
                  <td className="url">{rutaLegible(b.principal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtradas.length > cuantas && (
          <button type="button" className="acc sec" onClick={() => setCuantas((c) => c + 100)}>
            Ver más
          </button>
        )}
      </Caja>
    </div>
  );
}

function ListaDeCambios({ titulo, tono, filas }: { titulo: string; tono: "mejor" | "peor"; filas: { q: string; d: string }[] }) {
  return (
    <div className="lista-cambios">
      <h4 className={tono}>
        {titulo} <span className="n">{filas.length}</span>
      </h4>
      {filas.length === 0 ? (
        <p className="pista">Ninguna.</p>
      ) : (
        <ul>
          {filas.slice(0, 10).map((f) => (
            <li key={f.q}>
              <span>{f.q}</span>
              <span className="sub">{f.d}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Competencia ─────────────────────────────────────────────────────────────

interface DatosDeCompetencia {
  desde: string | null;
  hasta: string | null;
  competidores: {
    nombre: string;
    web: string | null;
    busquedas: number;
    keywords: string[];
    ias: string[];
    puestoMedio: number | null;
    porQue: string[];
    teGanaEn: number;
  }[];
  tuyas: { medidas: number; sales: number };
  sinSalir: string[];
}

export function CompetenciaDelCliente({ clienteId }: { clienteId: string }) {
  const { datos, cargando, error } = useJson<DatosDeCompetencia>(`/api/memoria/clientes/${clienteId}/seo/competencia`);
  const [abierto, setAbierto] = useState<string | null>(null);
  if (!datos) return <Cargando cargando={cargando} error={error} />;
  if (!datos.hasta)
    return <p className="grafico-vacio">Todavía no se ha medido a este cliente en las IAs. Se mide solo: ChatGPT cada semana y Claude y GPT cada mes.</p>;
  return (
    <div className="fondo">
      <h3 className="fondo-titulo">Quién sale en tu lugar</h3>
      <p className="pista">
        Cuando se le pregunta a una IA por lo que hace el cliente, éstos son los negocios que recomienda, de {mesCorto(datos.desde!)} a {mesCorto(datos.hasta)}. El cliente sale en{" "}
        <strong>
          {datos.tuyas.sales} de {datos.tuyas.medidas}
        </strong>{" "}
        respuestas. La competencia en Google (quién sale por encima en cada búsqueda) necesitaría la API de SE Ranking, que no está conectada.
      </p>
      {datos.sinSalir.length > 0 && (
        <p className="pista">
          <strong>No sale en ninguna IA</strong> en: {datos.sinSalir.join(" · ")}
        </p>
      )}
      <div className="tabla-envoltura">
        <table className="tabla-fondo">
          <thead>
            <tr>
              <th>Negocio</th>
              <th className="num">
                Búsquedas <Ayuda texto="En cuántas de las búsquedas medidas lo recomienda alguna IA." />
              </th>
              <th className="num">
                Por delante <Ayuda texto="En cuántas de esas búsquedas sale por delante del cliente, o el cliente no sale." />
              </th>
              <th className="num">Puesto medio</th>
              <th>En qué IAs</th>
              <th>Por qué lo recomiendan</th>
            </tr>
          </thead>
          <tbody>
            {datos.competidores.map((c) => (
              <Fragment key={c.nombre}>
                <tr onClick={() => setAbierto(abierto === c.nombre ? null : c.nombre)}>
                  <td>
                    <strong>{c.nombre}</strong>
                    {c.web && (
                      <a className="salir" href={c.web} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                        ↗
                      </a>
                    )}
                  </td>
                  <td className="num">{c.busquedas}</td>
                  <td className="num">
                    <span className={c.teGanaEn > 0 ? "peor" : "sin"}>{c.teGanaEn}</span>
                  </td>
                  <td className="num">{n(c.puestoMedio)}</td>
                  <td>{c.ias.join(", ")}</td>
                  <td className="por-que">{c.porQue[0] ?? "—"}</td>
                </tr>
                {abierto === c.nombre && (
                  <tr className="detalle">
                    <td colSpan={6}>
                      <p className="pista">Sale en: {c.keywords.join(" · ")}</p>
                      {c.porQue.slice(1).map((p) => (
                        <p className="pista" key={p}>
                          «{p}»
                        </p>
                      ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── El mapa de secciones (Estrategia) ───────────────────────────────────────

interface KeywordDeSeccion {
  texto: string;
  tipo: "servicio" | "informativa";
  principal: boolean;
  posicion: number | null;
  volumen: number | null;
  enGoogle: string | null;
}

interface Articulo {
  titulo: string;
  keyword: string | null;
  estado: string;
  cuando: string | null;
  de: "calendario" | "plan";
}

interface DatosDeSecciones {
  mes: string | null;
  secciones: {
    url: string;
    ruta: string;
    clics: number | null;
    impresiones: number | null;
    posicion: number | null;
    sesiones: number | null;
    keywords: KeywordDeSeccion[];
    enlazadaDesde: string[] | null;
    articulos: Articulo[];
    deServicio: number;
    deBlog: number;
  }[];
  sinPagina: (KeywordDeSeccion & { articulos: Articulo[] })[];
  todas: { texto: string; tipo: "servicio" | "informativa"; pagina: string | null }[];
  paginasDeLaWeb: string[];
}

export interface FichaDevuelta {
  [k: string]: unknown;
}

export function MapaDeSecciones({
  clienteId,
  mes,
  alGuardar,
}: {
  clienteId: string;
  mes: string | null;
  /** La ficha que devuelve el servidor tras guardar, para que el resto de la pantalla no se quede atrás. */
  alGuardar?: (ficha: FichaDevuelta) => void;
}) {
  const { datos, cargando, error, recargar } = useJson<DatosDeSecciones>(
    `/api/memoria/clientes/${clienteId}/seo/secciones${mes ? `?mes=${mes}` : ""}`,
  );
  const [ocupado, setOcupado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<string | null>(null);

  if (!datos) return <Cargando cargando={cargando} error={error} />;

  const paginas = [...new Set([...datos.secciones.map((s) => s.url), ...datos.paginasDeLaWeb])];

  async function asignar(texto: string, pagina: string | null) {
    if (!datos) return;
    setOcupado(true);
    setFallo(null);
    const keywords = datos.todas.map((k) => (k.texto === texto ? { ...k, pagina } : k));
    const r = await mandar(`/api/memoria/clientes/${clienteId}/seo`, "PUT", { keywords });
    setOcupado(false);
    if (!r.ok) return setFallo(String(r.datos.error ?? "No se pudo guardar."));
    alGuardar?.(r.datos.ficha as FichaDevuelta);
    recargar();
  }

  const posicion = (k: KeywordDeSeccion) =>
    k.posicion === null ? <span className="sin">sin posición</span> : <span className={k.posicion <= 10 ? "mejor" : k.posicion <= 20 ? "aviso" : "peor"}>{k.posicion}º</span>;

  return (
    <div className="fondo mapa-secciones">
      <h3 className="fondo-titulo">
        Las secciones de la web y lo que las apoya
        <Ayuda texto="Cada página de servicio de la estrategia con sus keywords, dónde salen en Google, qué páginas de la web la enlazan y qué artículos del blog la apoyan. Para cambiar qué página ataca una keyword, elígela en el desplegable; se guarda en la estrategia." />
      </h3>
      {fallo && <p className="peor">{fallo}</p>}
      {datos.secciones.length === 0 && (
        <p className="grafico-vacio">Ninguna keyword de la estrategia tiene página asignada todavía. Asígnalas abajo.</p>
      )}
      <div className="secciones">
        {datos.secciones.map((s) => {
          const flojo = s.enlazadaDesde !== null && s.enlazadaDesde.length < 3;
          return (
            <section className="seccion-card" key={s.url}>
              <header>
                <a href={s.url} target="_blank" rel="noreferrer" className="seccion-url">
                  {rutaLegible(s.url)} ↗
                </a>
                <span className="pista">
                  {s.clics === null ? "sin datos de Search Console" : `${n(s.clics)} clics · ${n(s.impresiones)} impr. · pos. ${n(s.posicion)}`}
                  {s.sesiones !== null && ` · ${n(s.sesiones)} visitas`}
                </span>
              </header>
              <ul className="kws-seccion">
                {s.keywords.map((k) => (
                  <li key={k.texto}>
                    <span className="kw">
                      {k.principal ? "★ " : ""}
                      {k.texto}
                      {k.tipo === "informativa" && <span className="sub"> · blog</span>}
                    </span>
                    {posicion(k)}
                    {k.enGoogle && rutaLegible(k.enGoogle) !== rutaLegible(s.url) && (
                      <span className="pill aviso" title={`Google enseña ${k.enGoogle}`}>
                        sale otra
                      </span>
                    )}
                    <select value={s.url} disabled={ocupado} onChange={(e) => void asignar(k.texto, e.target.value || null)} aria-label={`Página de ${k.texto}`}>
                      <option value="">— sin página —</option>
                      {paginas.map((p) => (
                        <option key={p} value={p}>
                          {rutaLegible(p)}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
              <div className="apoyos">
                <button type="button" className="enlace" onClick={() => setAbierta(abierta === s.url ? null : s.url)}>
                  {s.enlazadaDesde === null ? "Enlaces internos: sin medir" : `La enlazan ${s.enlazadaDesde.length} páginas de la web`}
                </button>
                {flojo && <span className="pill peor">pocos enlaces internos</span>}
                <span className={s.articulos.length ? "pill mejor" : "pill aviso"}>
                  {s.articulos.length ? `${s.articulos.length} artículos la apoyan` : "ningún artículo la apoya"}
                </span>
              </div>
              {abierta === s.url && (
                <div className="apoyos-detalle">
                  {(s.enlazadaDesde ?? []).length > 0 && (
                    <ul>
                      {(s.enlazadaDesde ?? []).map((d) => (
                        <li key={d}>{rutaLegible(d)}</li>
                      ))}
                    </ul>
                  )}
                  {s.articulos.map((a) => (
                    <p className="pista" key={a.titulo}>
                      ✎ {a.titulo} · {a.estado}
                      {a.cuando && ` · ${a.cuando}`}
                    </p>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {datos.sinPagina.length > 0 && (
        <Caja
          titulo={`Keywords sin página (${datos.sinPagina.length})`}
          ayuda="Keywords de la estrategia que todavía no tienen una página que las ataque. Las de servicio: elige la página que debe posicionar. Las de blog: hay que escribir el artículo (desde Contenidos → Planes del mes) y luego asignarlo aquí."
        >
          <ul className="kws-seccion">
            {datos.sinPagina.map((k) => (
              <li key={k.texto}>
                <span className="kw">
                  {k.principal ? "★ " : ""}
                  {k.texto}
                  <span className="sub"> · {k.tipo === "informativa" ? "blog" : "servicio"}</span>
                </span>
                {posicion(k)}
                {k.articulos.length > 0 && <span className="pill">{k.articulos.length} en el plan</span>}
                <select value="" disabled={ocupado} onChange={(e) => e.target.value && void asignar(k.texto, e.target.value)} aria-label={`Página de ${k.texto}`}>
                  <option value="">Asignar página…</option>
                  {k.enGoogle && <option value={k.enGoogle}>{rutaLegible(k.enGoogle)} (la que ya sale)</option>}
                  {paginas.map((p) => (
                    <option key={p} value={p}>
                      {rutaLegible(p)}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </Caja>
      )}
    </div>
  );
}

// ── Ideas de keywords nuevas (Google Ads) ───────────────────────────────────

interface DatosDeIdeas {
  zona: string;
  ideas: { texto: string; busquedas: number; competencia: string | null; cpc: number | null; posicion: number | null; impresiones: number | null }[];
  todas: { texto: string; tipo: "servicio" | "informativa"; pagina: string | null }[];
  error: string | null;
}

const COMPETENCIA: Record<string, [string, string]> = { LOW: ["baja", "mejor"], MEDIUM: ["media", "aviso"], HIGH: ["alta", "peor"] };

export function IdeasDeKeywords({ clienteId, alGuardar }: { clienteId: string; alGuardar?: (ficha: FichaDevuelta) => void }) {
  const [pedidas, setPedidas] = useState(false);
  const { datos, cargando, error, recargar } = useJson<DatosDeIdeas>(pedidas ? `/api/memoria/clientes/${clienteId}/seo/ideas-de-keywords` : null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [anadidas, setAnadidas] = useState<Set<string>>(new Set());
  useEffect(() => {
    setPedidas(false);
    setAnadidas(new Set());
  }, [clienteId]);

  async function anadir(texto: string, tipo: "servicio" | "informativa") {
    if (!datos) return;
    setOcupado(texto);
    setFallo(null);
    const keywords = [...datos.todas, { texto, tipo, pagina: null }];
    const r = await mandar(`/api/memoria/clientes/${clienteId}/seo`, "PUT", { keywords });
    setOcupado(null);
    if (!r.ok) return setFallo(String(r.datos.error ?? "No se pudo añadir."));
    setAnadidas((a) => new Set([...a, texto]));
    alGuardar?.(r.datos.ficha as FichaDevuelta);
    recargar();
  }

  return (
    <Caja
      titulo={`Keywords nuevas con búsquedas${datos ? ` en ${datos.zona}` : ""}`}
      fuente="Google Ads"
      ayuda="Ideas de Google Ads alrededor de lo que ya trabaja el cliente: lo que la gente busca en su zona y todavía no está en la estrategia. Se quitan las que no comparten tema (marcas de otros, productos sueltos). «Ya sale» es que Google ya le enseña por ella: empujar es más fácil que empezar. Al añadir una entra sin página; luego se le asigna en el mapa de arriba."
    >
      {!pedidas ? (
        <button type="button" className="acc" onClick={() => setPedidas(true)}>
          Buscar ideas en Google Ads
        </button>
      ) : (
        <>
          <Cargando cargando={cargando} error={error ?? datos?.error ?? null} vacio={datos && !datos.error && datos.ideas.length === 0 ? "Google Ads no da ideas nuevas que encajen con esta estrategia." : null} />
          {fallo && <p className="peor">{fallo}</p>}
          {datos && datos.ideas.length > 0 && (
            <div className="tabla-envoltura">
              <table className="tabla-fondo compacta">
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th className="num">Al mes</th>
                    <th>Competencia</th>
                    <th className="num">€/clic</th>
                    <th className="num">Ya sale</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {datos.ideas.map((i) => {
                    const comp = i.competencia ? COMPETENCIA[i.competencia] : undefined;
                    const hecha = anadidas.has(i.texto);
                    return (
                      <tr key={i.texto}>
                        <td>{i.texto}</td>
                        <td className="num">{n(i.busquedas)}</td>
                        <td>{comp ? <span className={`pill ${comp[1]}`}>{comp[0]}</span> : <span className="sin">—</span>}</td>
                        <td className="num">{i.cpc ? eurClic(i.cpc) : <span className="sin">—</span>}</td>
                        <td className="num">{i.posicion ? `${n(i.posicion)}º` : <span className="sin">no</span>}</td>
                        <td className="botones-idea">
                          {hecha ? (
                            <span className="pill mejor">añadida</span>
                          ) : (
                            <>
                              <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void anadir(i.texto, "servicio")} title="Añadir a la estrategia como keyword de servicio">
                                + servicio
                              </button>
                              <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void anadir(i.texto, "informativa")} title="Añadir a la estrategia como keyword de blog">
                                + blog
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Caja>
  );
}

// ── El trabajo: acciones y objetivos ────────────────────────────────────────

interface Accion {
  id: string;
  mes: string;
  tipo: string;
  queHacer: string;
  porQue: string;
  url: string | null;
  consulta: string | null;
  metrica: string;
  estado: string;
  enJuego: number | null;
  valorAlProponer: number;
  valorAlAplicar: number | null;
  valorAlRevisar: number | null;
  aplicadoEl: string | null;
  revisadoEl: string | null;
  nota: string | null;
  enTrello: boolean;
  creada: string;
}

interface Objetivo {
  id: string;
  tipo: string;
  descripcion: string;
  meta: number;
  partida: number | null;
  fechaLimite: string | null;
  estado: string;
}

const ESTADOS_DE_ACCION = [
  ["propuesta", "Por hacer"],
  ["hecha", "Hechas"],
  ["descartada", "Descartadas"],
  ["todas", "Todas"],
] as const;

export function TrabajoDelCliente({ clienteId }: { clienteId: string }) {
  const acciones = useJson<{ acciones: Accion[] }>(`/api/memoria/clientes/${clienteId}/seo/acciones`);
  const objetivos = useJson<{ objetivos: Objetivo[] }>(`/api/memoria/clientes/${clienteId}/seo/objetivos`);
  const [filtro, setFiltro] = useState<(typeof ESTADOS_DE_ACCION)[number][0]>("propuesta");
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [nueva, setNueva] = useState({ queHacer: "", porQue: "", url: "" });
  const [obj, setObj] = useState({ descripcion: "", meta: "", partida: "", fechaLimite: "" });
  const { preguntar, dialogo } = useDialogo();

  async function hacer(clave: string, url: string, metodo: string, cuerpo?: unknown, recargar?: () => void) {
    setOcupado(clave);
    setFallo(null);
    const r = await mandar(url, metodo, cuerpo);
    setOcupado(null);
    if (!r.ok) setFallo(String(r.datos.error ?? "No se pudo."));
    recargar?.();
    return r.ok;
  }

  const lista = (acciones.datos?.acciones ?? []).filter((a) => filtro === "todas" || a.estado === filtro || (filtro === "propuesta" && a.estado === "aplicada"));
  const cuenta = (e: string) => (acciones.datos?.acciones ?? []).filter((a) => e === "todas" || a.estado === e).length;

  return (
    <div className="fondo trabajo">
      {dialogo}
      {fallo && <p className="peor">{fallo}</p>}
      <Caja
        titulo="El trabajo SEO"
        ayuda="Todo lo que se ha decidido hacer en este cliente, mes a mes: lo propone BilboBot desde la auditoría y el informe, o lo apuntas tú. Al marcarla hecha queda la medida del momento, y al mes se compara para saber si funcionó."
      >
        <div className="fondo-filtros">
          {ESTADOS_DE_ACCION.map(([id, nombre]) => (
            <button key={id} type="button" className={filtro === id ? "chip activo" : "chip"} onClick={() => setFiltro(id)}>
              {nombre} <span className="n">{cuenta(id)}</span>
            </button>
          ))}
        </div>
        <Cargando cargando={acciones.cargando} error={acciones.error} vacio={acciones.datos && lista.length === 0 ? "Nada en esta lista." : null} />
        <div className="acciones">
          {lista.map((a) => (
            <div className={`accion-fila ${a.estado}`} key={a.id}>
              <div className="accion-que">
                <strong>{a.queHacer}</strong>
                <span className="pista">{a.porQue}</span>
                <span className="sub">
                  {mesCorto(a.mes)}
                  {a.url && ` · ${rutaLegible(a.url)}`}
                  {a.consulta && ` · «${a.consulta}»`}
                  {a.enJuego ? ` · en juego ${n(a.enJuego)} ${a.metrica}` : ""}
                </span>
                {(a.valorAlAplicar !== null || a.valorAlRevisar !== null) && (
                  <span className="sub">
                    {a.metrica}: {n(a.valorAlProponer)} al proponerla
                    {a.valorAlAplicar !== null && ` → ${n(a.valorAlAplicar)} al hacerla`}
                    {a.valorAlRevisar !== null && (
                      <>
                        {" → "}
                        <span className={a.valorAlRevisar >= (a.valorAlAplicar ?? a.valorAlProponer) ? "mejor" : "peor"}>{n(a.valorAlRevisar)} al revisarla</span>
                      </>
                    )}
                  </span>
                )}
              </div>
              <div className="accion-botones">
                <span className={`pill ${a.estado === "hecha" ? "mejor" : a.estado === "descartada" ? "" : "aviso"}`}>{a.estado}</span>
                {a.estado !== "hecha" && (
                  <button type="button" className="acc" disabled={!!ocupado} onClick={() => void hacer(a.id, `/api/memoria/clientes/${clienteId}/acciones/${a.id}`, "PATCH", { estado: "hecha" }, acciones.recargar)}>
                    Hecha
                  </button>
                )}
                {a.estado !== "descartada" && a.estado !== "hecha" && (
                  <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void hacer(a.id, `/api/memoria/clientes/${clienteId}/acciones/${a.id}`, "PATCH", { estado: "descartada" }, acciones.recargar)}>
                    Descartar
                  </button>
                )}
                {a.estado !== "propuesta" && (
                  <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void hacer(a.id, `/api/memoria/clientes/${clienteId}/acciones/${a.id}`, "PATCH", { estado: "propuesta" }, acciones.recargar)}>
                    Reabrir
                  </button>
                )}
                {!a.enTrello && a.estado === "propuesta" && (
                  <button
                    type="button"
                    className="acc sec"
                    disabled={!!ocupado}
                    onClick={async () => {
                      if (await preguntar(`¿Mandar «${a.queHacer}» a la ficha de Trello del cliente?`, "Mandar a Trello"))
                        void hacer(a.id, `/api/memoria/clientes/${clienteId}/acciones/${a.id}/trello`, "POST", {}, acciones.recargar);
                    }}
                  >
                    A Trello
                  </button>
                )}
                {a.enTrello && <span className="pill">en Trello</span>}
              </div>
            </div>
          ))}
        </div>
        <form
          className="nueva-accion"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!nueva.queHacer.trim()) return;
            const ok = await hacer("nueva", `/api/memoria/clientes/${clienteId}/acciones`, "POST", { queHacer: nueva.queHacer.trim(), porQue: nueva.porQue.trim(), url: nueva.url.trim() || undefined }, acciones.recargar);
            if (ok) setNueva({ queHacer: "", porQue: "", url: "" });
          }}
        >
          <input value={nueva.queHacer} onChange={(e) => setNueva({ ...nueva, queHacer: e.target.value })} placeholder="Qué hay que hacer (p. ej. «Reescribir el título de /limpieza-cristales»)" />
          <input value={nueva.porQue} onChange={(e) => setNueva({ ...nueva, porQue: e.target.value })} placeholder="Por qué" />
          <input value={nueva.url} onChange={(e) => setNueva({ ...nueva, url: e.target.value })} placeholder="Página (opcional)" />
          <button type="submit" className="acc" disabled={!!ocupado || !nueva.queHacer.trim()}>
            + Añadir
          </button>
        </form>
      </Caja>

      <Caja titulo="Objetivos" ayuda="Lo que se quiere conseguir con este cliente, con su número de partida y su meta. Sin partida no se puede comprobar si se cumplió.">
        <Cargando cargando={objetivos.cargando} error={objetivos.error} vacio={objetivos.datos && objetivos.datos.objetivos.length === 0 ? "Ningún objetivo fijado." : null} />
        {(objetivos.datos?.objetivos ?? []).map((o) => (
          <div className={`accion-fila ${o.estado}`} key={o.id}>
            <div className="accion-que">
              <strong>{o.descripcion}</strong>
              <span className="sub">
                {o.partida === null ? "sin partida" : n(o.partida)} → <strong>{n(o.meta)}</strong>
                {o.fechaLimite && ` · antes de ${o.fechaLimite}`}
              </span>
            </div>
            <div className="accion-botones">
              <span className={`pill ${o.estado === "cumplido" ? "mejor" : o.estado === "abandonado" ? "" : "aviso"}`}>{o.estado}</span>
              {o.estado === "activo" && (
                <>
                  <button type="button" className="acc" disabled={!!ocupado} onClick={() => void hacer(o.id, `/api/memoria/clientes/${clienteId}/seo/objetivos/${o.id}`, "PATCH", { estado: "cumplido" }, objetivos.recargar)}>
                    Cumplido
                  </button>
                  <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void hacer(o.id, `/api/memoria/clientes/${clienteId}/seo/objetivos/${o.id}`, "PATCH", { estado: "abandonado" }, objetivos.recargar)}>
                    Abandonar
                  </button>
                </>
              )}
              {o.estado !== "activo" && (
                <button type="button" className="acc sec" disabled={!!ocupado} onClick={() => void hacer(o.id, `/api/memoria/clientes/${clienteId}/seo/objetivos/${o.id}`, "PATCH", { estado: "activo" }, objetivos.recargar)}>
                  Reactivar
                </button>
              )}
            </div>
          </div>
        ))}
        <form
          className="nueva-accion"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await hacer(
              "obj",
              `/api/memoria/clientes/${clienteId}/seo/objetivos`,
              "POST",
              {
                descripcion: obj.descripcion.trim(),
                meta: Number(obj.meta.replace(",", ".")),
                partida: obj.partida.trim() ? Number(obj.partida.replace(",", ".")) : null,
                fechaLimite: obj.fechaLimite || null,
              },
              objetivos.recargar,
            );
            if (ok) setObj({ descripcion: "", meta: "", partida: "", fechaLimite: "" });
          }}
        >
          <input value={obj.descripcion} onChange={(e) => setObj({ ...obj, descripcion: e.target.value })} placeholder="Qué (p. ej. «Clics al mes desde Google»)" />
          <input value={obj.partida} onChange={(e) => setObj({ ...obj, partida: e.target.value })} placeholder="Partida" inputMode="decimal" />
          <input value={obj.meta} onChange={(e) => setObj({ ...obj, meta: e.target.value })} placeholder="Meta" inputMode="decimal" />
          <input type="month" value={obj.fechaLimite} onChange={(e) => setObj({ ...obj, fechaLimite: e.target.value })} aria-label="Fecha límite" />
          <button type="submit" className="acc" disabled={!!ocupado || !obj.descripcion.trim() || !obj.meta.trim()}>
            + Objetivo
          </button>
        </form>
      </Caja>
    </div>
  );
}

// ── El informe del mes, desde el panel ──────────────────────────────────────

export function GenerarInforme({ clienteId, meses, alHacer }: { clienteId: string; meses: { mes: string; hecho: boolean }[]; alHacer: () => void }) {
  const { preguntar, pedir, dialogo } = useDialogo();
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const actual = new Date().toISOString().slice(0, 7);
  const cerrados = meses.filter((m) => m.mes < actual);
  const [mes, setMes] = useState(cerrados[0]?.mes ?? "");
  useEffect(() => setMes(cerrados[0]?.mes ?? ""), [clienteId, cerrados[0]?.mes]);
  if (cerrados.length === 0) return null;
  const elegido = cerrados.find((m) => m.mes === mes);

  async function generar() {
    if (!elegido) return;
    let cuerpo: Record<string, unknown> = {};
    if (elegido.hecho) {
      const porQue = await pedir(`El informe de ${mesCorto(mes)} ya existe y está congelado. Si lo rehaces, la versión de ahora se guarda aparte. ¿Qué estaba mal? (una frase)`);
      if (!porQue?.trim()) return;
      cuerpo = { rehacer: true, porQue: porQue.trim() };
    } else if (!(await preguntar(`¿Generar el informe de ${mesCorto(mes)}? Pide los datos a Google y lo deja congelado.`, "Generar"))) return;
    setOcupado(true);
    setMensaje("Generando… puede tardar un minuto.");
    const r = await mandar(`/api/memoria/clientes/${clienteId}/informes/${mes}/generar`, "POST", cuerpo);
    setOcupado(false);
    setMensaje(r.ok ? `Hecho: informe de ${mesCorto(mes)} ${elegido.hecho ? "rehecho" : "generado"}.` : String(r.datos.error ?? "No se pudo generar."));
    if (r.ok) alHacer();
  }

  return (
    <div className="generar-informe">
      {dialogo}
      <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mes del informe" disabled={ocupado}>
        {cerrados.map((m) => (
          <option key={m.mes} value={m.mes}>
            {mesCorto(m.mes)}
            {m.hecho ? " · ya hecho" : " · sin hacer"}
          </option>
        ))}
      </select>
      <button type="button" className="acc" onClick={() => void generar()} disabled={ocupado || !elegido}>
        {elegido?.hecho ? "Rehacer el informe" : "Generar el informe"}
      </button>
      {mensaje && <span className="pista">{mensaje}</span>}
    </div>
  );
}
